import * as actions from 'actions/feature/logicNodes'
import { Map, fromJS } from 'immutable'
import { NATIVE_INPUT, SIMPLE, TIMER } from 'utils/constants'
import {
  set,
  setIn,
  updateIn,
  removeIn,
  removeAllIn,
  updateAllIn,
  findKeysBy,
  getEq,
  mergeIn,
  filter,
  filterNot,
  pluck,
  merge,
  getIn,
  getInEq,
  valueSeq,
  push,
} from 'utils/immutable'
import {
  makeLogicNode,
  defaultLogicNodeProps,
  defaultConditionProps,
} from 'utils/feature'
import { EVAL_VALUES } from 'components/utils'
import { compose, omit, identity } from 'ramda'

export default {
  [actions.DELETE_LOGIC_NODE]: deleteLogicNode,
  [actions.RESET_LOGIC_NODE]: resetLogicNode,
  [actions.ADD_LOGIC_NODE]: addLogicNode,
}

const queriesPath = ['currentFeature', 'queries']
const conditionsPath = ['currentFeature', 'conditions']
const featurePath = ['currentFeature', 'feature']
const outputMapsPath = ['currentFeature', 'outputMaps']
const outputsPath = ['currentFeature', 'outputs']

export function addLogicNode(state, { nodeType, nextIndex, logicDetails }) {
  const featureId = state.getIn(featurePath).first().get('id')

  const { logicNode, conditions } =
    logicDetails || makeLogicNode(nodeType, nextIndex)

  const { uri } = logicNode

  const setLogicNode = setIn([...queriesPath, uri], logicNode)
  const mergeConditions = mergeIn(conditionsPath, fromJS(conditions))
  const setInFeatureList = updateIn(
    [...featurePath, featureId, 'queries'],
    push(
      Map({
        id: uri,
        schema: 'queries',
      })
    )
  )

  const transform = compose(setInFeatureList, setLogicNode, mergeConditions)

  return transform(state)
}

function deleteLogicNode(state, { uri }) {
  const featureId = state.getIn(featurePath).first().get('id')
  const outputs = state.getIn(outputsPath)
  const conditions = state.getIn(conditionsPath)

  const getConditionsToClearUris = compose(
    pluck('uri'),
    valueSeq,
    filter(getEq('refQueryUri', uri))
  )
  const getOutputQueryFnUris = compose(
    findKeysBy(getEq('outputQuery', uri)),
    getIn(outputMapsPath)
  )

  const getOutputQueryPath = (key) => [...outputMapsPath, key, 'outputQuery']
  const outputQueryFnUris = getOutputQueryFnUris(state)

  const resetOutputQueries = removeAllIn(outputQueryFnUris, getOutputQueryPath)

  const queryConditions = state.getIn([...queriesPath, uri, 'conditions'])

  const removeConditions = removeAllIn(queryConditions, conditionsPath)

  const clearConditionsWithRefQuery = updateAllIn(
    getConditionsToClearUris(conditions),
    conditionsPath,
    merge(
      Map({
        refQueryUri: undefined,
        queryType: SIMPLE,
        subType: NATIVE_INPUT,
      })
    )
  )

  const removeQuery = removeIn([...queriesPath, uri])
  const removeQueryFromFeature = updateIn(
    [...featurePath, featureId, 'queries'],
    filterNot(getEq('id', uri))
  )

  // if the node being deleted is an output query and a timer
  // then we need to reset the evaluation for all outputs associated to true
  const isTimerOutputQuery =
    !outputQueryFnUris.isEmpty() &&
    getInEq([...queriesPath, uri, 'queryType'], TIMER, state)

  const getOutputIds = compose(
    pluck('id'),
    valueSeq,
    filter((output) => outputQueryFnUris.includes(output.get('fnId')))
  )

  const outputIds = getOutputIds(outputs)

  const resetOutputsEval = isTimerOutputQuery
    ? updateAllIn(outputIds, outputsPath, set('value', EVAL_VALUES.TRUE))
    : identity

  const transform = compose(
    resetOutputsEval,
    removeQueryFromFeature,
    removeQuery,
    removeConditions,
    resetOutputQueries,
    clearConditionsWithRefQuery
  )

  return transform(state)
}

function resetLogicNode(state, { uri, nodeType }) {
  const conditionUris = state.getIn([
    'currentFeature',
    'queries',
    uri,
    'conditions',
  ])

  const resetConditions = updateAllIn(
    conditionUris,
    ['currentFeature', 'conditions'],
    merge(Map(defaultConditionProps()))
  )

  const resetNode = updateIn(
    ['currentFeature', 'queries', uri],
    merge(
      Map(omit(['description', 'conditions'], defaultLogicNodeProps[nodeType]))
    )
  )

  const transform = compose(resetNode, resetConditions)

  return transform(state)
}
